import type { EntryInput, SyncResult } from '@bnn/shared';

/**
 * Outbox (Requirements §9.3). The scan screen never calls the API directly: every entry and
 * void goes through here, is stored first, and is marked SYNCED only after the server ACKs it.
 * M2 uses an in-memory store; M3 plugs in an IndexedDB store with the same interface.
 */

export type OpStatus = 'PENDING' | 'SYNCED' | 'REJECTED' | 'CANCELLED';

interface OpBase {
  /** entry id or void op_id — the idempotency key sent to the server */
  key: string;
  session_id: string;
  /** who made it: ops are only ever sent with that user's own login (ARCHITECTURE §5) */
  user_id: string;
  status: OpStatus;
  created_at: string;
  /**
   * Send order on this device. Strictly increasing even when two ops share a millisecond or the
   * clock is set back (e.g. corrected when the phone reconnects). Missing on ops stored by an
   * older version: those sort first, by created_at.
   */
  seq?: number;
  attempts: number;
  error_code?: string;
  error?: string;
  flags?: string[];
}

export interface EntryOp extends OpBase {
  kind: 'ENTRY';
  entry: EntryInput;
  /** display-only snapshot */
  item_name: string;
  /** set when the user voided this entry before it was ever sent */
  local_void_reason?: string;
}

export interface VoidOp extends OpBase {
  kind: 'VOID';
  entry_id: string;
  reason: string;
}

export type OutboxOp = EntryOp | VoidOp;

export interface OutboxStore {
  all(): Promise<OutboxOp[]>;
  put(op: OutboxOp): Promise<void>;
  putMany(ops: OutboxOp[]): Promise<void>;
  /** only ever called for ops the server already confirmed (or cancelled locally) */
  remove(keys: string[]): Promise<void>;
}

export interface OutboxSender {
  syncEntries(entries: EntryInput[]): Promise<SyncResult[]>;
  syncVoids(ops: { op_id: string; type: 'VOID'; entry_id: string; reason: string; created_at: string }[]): Promise<SyncResult[]>;
}

/** Thrown by a sender when the server could not be reached (keep ops PENDING and retry). */
export class OfflineError extends Error {}

export class MemoryOutboxStore implements OutboxStore {
  private ops = new Map<string, OutboxOp>();
  async all() {
    return [...this.ops.values()].map((o) => structuredClone(o));
  }
  async put(op: OutboxOp) {
    this.ops.set(op.key, structuredClone(op));
  }
  async putMany(ops: OutboxOp[]) {
    for (const o of ops) this.ops.set(o.key, structuredClone(o));
  }
  async remove(keys: string[]) {
    for (const k of keys) this.ops.delete(k);
  }
}

const BATCH = 100;
/** Confirmed / cancelled ops are kept this long for the "recent" list, then pruned. */
const KEEP_DONE_MS = 48 * 60 * 60 * 1000;
const RETRY_MS = [2_000, 5_000, 10_000, 30_000, 60_000];

export class Outbox {
  private listeners = new Set<() => void>();
  private cache: OutboxOp[] = [];
  private flushing: Promise<void> | null = null;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private failures = 0;
  online = true;
  lastError: string | null = null;
  lastSyncAt: string | null = null;
  private userId: string | null = null;
  private ready: Promise<void> | null = null;

  constructor(
    private store: OutboxStore,
    private sender: OutboxSender,
    private opts: { autoRetry?: boolean } = { autoRetry: true },
  ) {}

  /** Load what is stored on the device (survives reloads); prune old confirmed ops. Idempotent. */
  init(): Promise<void> {
    this.ready ??= (async () => {
      const all = await this.store.all();
      const cutoff = Date.now() - KEEP_DONE_MS;
      const old = all.filter((o) => (o.status === 'SYNCED' || o.status === 'CANCELLED') && Date.parse(o.created_at) < cutoff);
      if (old.length) await this.store.remove(old.map((o) => o.key));
      const gone = new Set(old.map((o) => o.key));
      this.cache = all.filter((o) => !gone.has(o.key));
      this.emit();
    })();
    return this.ready;
  }

  /** Who is logged in. Only this user's ops are sent; others wait for their own login. */
  setUser(userId: string | null) {
    this.userId = userId;
    this.emit();
    if (userId) void this.flush();
  }

  get currentUser() {
    return this.userId;
  }

  subscribe(fn: () => void) {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  /** All ops, oldest first. */
  ops(): readonly OutboxOp[] {
    return this.cache;
  }

  /** Pending ops of the current user (optionally of one session). */
  pendingCount(sessionId?: string) {
    return this.pending(sessionId).length;
  }

  pending(sessionId?: string) {
    return this.cache.filter((o) => o.status === 'PENDING' && o.user_id === this.userId && (!sessionId || o.session_id === sessionId));
  }

  /** Sessions that still have pending ops of the current user (for the heartbeat). */
  pendingSessionIds() {
    return [...new Set(this.pending().map((o) => o.session_id))];
  }

  private requireUser() {
    if (!this.userId) throw new Error('ยังไม่ได้เข้าสู่ระบบ');
    return this.userId;
  }

  /** Next send-order number: never below the clock (keeps tabs roughly in time order), never repeats. */
  private nextSeq() {
    const last = this.cache.reduce((m, o) => Math.max(m, o.seq ?? 0), 0);
    return Math.max(Date.now(), last + 1);
  }

  private emit() {
    this.cache = [...this.cache].sort(
      (a, b) => (a.seq ?? -1) - (b.seq ?? -1) || a.created_at.localeCompare(b.created_at) || a.key.localeCompare(b.key),
    );
    for (const fn of this.listeners) fn();
  }

  private async save(ops: OutboxOp[]) {
    await this.store.putMany(ops);
    const byKey = new Map(ops.map((o) => [o.key, o]));
    this.cache = [...this.cache.filter((o) => !byKey.has(o.key)), ...ops];
    this.emit();
  }

  async addEntry(entry: EntryInput, itemName: string) {
    await this.init(); // never let the initial load overwrite a fresh op
    const op: EntryOp = {
      kind: 'ENTRY',
      key: entry.id,
      session_id: entry.session_id,
      user_id: this.requireUser(),
      status: 'PENDING',
      created_at: new Date().toISOString(),
      seq: this.nextSeq(),
      attempts: 0,
      entry,
      item_name: itemName,
    };
    await this.save([op]);
    void this.flush();
    return op;
  }

  /**
   * Void an entry. If it was never sent, cancel it locally (it never reaches the server);
   * otherwise queue a VOID operation with its own idempotency key.
   */
  async addVoid(entryId: string, sessionId: string, reason: string) {
    await this.init();
    const local = this.cache.find((o): o is EntryOp => o.kind === 'ENTRY' && o.key === entryId);
    if (local && local.status === 'PENDING' && !this.inFlight.has(entryId)) {
      await this.save([{ ...local, status: 'CANCELLED', local_void_reason: reason }]);
      return;
    }
    const op: VoidOp = {
      kind: 'VOID',
      key: crypto.randomUUID(),
      session_id: sessionId,
      user_id: this.requireUser(),
      status: 'PENDING',
      created_at: new Date().toISOString(),
      seq: this.nextSeq(),
      attempts: 0,
      entry_id: entryId,
      reason,
    };
    await this.save([op]);
    void this.flush();
  }

  private inFlight = new Set<string>();

  /** Send pending ops in creation order. Safe to call any time; concurrent calls share one run. */
  flush(): Promise<void> {
    this.flushing ??= this.run().finally(() => {
      this.flushing = null;
    });
    return this.flushing;
  }

  private async run() {
    if (this.retryTimer) {
      clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }
    await this.init();
    for (;;) {
      const pending = this.pending();
      if (!pending.length) break;
      // Consecutive ops of the same kind form one batch, so a re-count after a void keeps its order.
      const kind = pending[0]!.kind;
      const batch: OutboxOp[] = [];
      for (const o of pending) {
        if (o.kind !== kind || batch.length >= BATCH) break;
        batch.push(o);
      }
      batch.forEach((o) => this.inFlight.add(o.key));
      let results: SyncResult[];
      try {
        results =
          kind === 'ENTRY'
            ? await this.sender.syncEntries((batch as EntryOp[]).map((o) => o.entry))
            : await this.sender.syncVoids(
                (batch as VoidOp[]).map((o) => ({ op_id: o.key, type: 'VOID' as const, entry_id: o.entry_id, reason: o.reason, created_at: o.created_at })),
              );
      } catch (e) {
        batch.forEach((o) => this.inFlight.delete(o.key));
        this.online = !(e instanceof OfflineError);
        this.lastError = e instanceof Error ? e.message : String(e);
        // Nothing is dropped: ops stay PENDING and are retried with backoff.
        await this.save(batch.map((o) => ({ ...o, attempts: o.attempts + 1 })));
        this.scheduleRetry();
        return;
      }
      batch.forEach((o) => this.inFlight.delete(o.key));
      this.online = true;
      this.lastError = null;
      this.lastSyncAt = new Date().toISOString();
      this.failures = 0;
      const byId = new Map(results.map((r) => [r.id, r]));
      await this.save(
        batch.map((o): OutboxOp => {
          const r = byId.get(o.key);
          if (!r) return { ...o, attempts: o.attempts + 1 }; // no answer for this op: retry later
          if (r.status === 'REJECTED') return { ...o, status: 'REJECTED', attempts: o.attempts + 1, error_code: r.error_code, error: r.message };
          return { ...o, status: 'SYNCED', attempts: o.attempts + 1, flags: r.flags };
        }),
      );
      if (batch.every((o) => !byId.has(o.key))) {
        this.scheduleRetry();
        return;
      }
    }
  }

  private scheduleRetry() {
    if (!this.opts.autoRetry || this.retryTimer) return;
    const ms = RETRY_MS[Math.min(this.failures++, RETRY_MS.length - 1)]!;
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      void this.flush();
    }, ms);
  }
}
